import {
  configureStore,
  isFulfilled,
  isPending,
  isRejected,
  isRejectedWithValue,
} from '@reduxjs/toolkit';
import { userReducer, setUser } from './slice/userSlice';
import { roleReducer } from './slice/roleSlice';
import { loadingReducer, setLoading } from './slice/loadingSlice';
import { setLightTheme, themeReducer } from './slice/themeSlice';
import { QApi, PublicQApi } from './baseQuery';
import Toast from 'react-native-toast-message';
import { errorLogOnServer, removeItemLocalStorage } from '../utils';
import { deleteFirebaseToken } from '../../firebase';
import { unreadReducer } from './slice/unreadSlice';
import firstLoginModalReducer from './slice/firstLoginModalSlice';
import qrReducer from './slices/qrSlice';

const loadingPrevent = [
  'getQueueManagedList',
  'getUserQueueJoinedList',
  'getNotificationList',
  'getUnreadCount',
  'getTransactionDetail',
  'getWalletPaymentTransactions',
  'getWalletBalanceLog',
  'getServiceOrderList',
  'getSalesStats',
  'getUserServiceOrderList',
  'getQueueServicesList',
  'getQueueDetail',
  'getUserQueueDetail',
  'queueUserDetailUpdate',
];

const rtkQueryErrorLogger = (api) => (next) => async (action) => {
  if (isRejectedWithValue(action)) {
    if (action.payload.status === 401 || action.payload.status === 403) {
      await removeItemLocalStorage('fcm_token');
      await removeItemLocalStorage('token');
      await removeItemLocalStorage('role');
      await deleteFirebaseToken();
      api.dispatch(setUser(null));
      api.dispatch(setLightTheme(false));
    } else if (
      (action?.payload?.status === 400 ||
        action?.payload?.status === 404 ||
        action?.payload?.status === 422) &&
      action?.payload?.data?.detail
    ) {
      // Skip toast notification for queue date errors - they're handled in UI
      const errorData = action?.payload?.data?.detail;
      const errorCode =
        typeof errorData === 'object' ? errorData?.error_code : null;

      // List of error codes that should be handled in UI instead of showing toast
      const uiHandledErrorCodes = [
        'QUEUE_ENDED',
        'BOOKING_NOT_OPEN',
        'ADVANCE_BOOKING_LIMIT',
        'PAST_DATE_NOT_ALLOWED',
        'QUEUE_CLOSED_WEEKENDS',
        'QUEUE_SUSPENDED',
      ];

      const isQueueDateError =
        errorCode && uiHandledErrorCodes.includes(errorCode);

      if (!isQueueDateError) {
        Toast.show({
          type: 'error',
          text1: 'Oops!',
          text2:
            action?.payload?.status === 422
              ? 'Error Q422'
              : action?.payload?.data?.detail,
        });
      }
      errorLogOnServer(action);
    } else {
      Toast.show({
        type: 'error',
        text1: 'Oops!',
        text2: 'Something went wrong',
      });
      errorLogOnServer(action);
    }
  }
  return next(action);
};

export const genericLoaderMiddleware = (store) => (next) => (action) => {
  if (action?.meta?.arg?.endpointName) {
    if (loadingPrevent.includes(action?.meta?.arg?.endpointName)) {
      return next(action);
    }
  }
  if (isPending(action)) {
    store.dispatch(setLoading(true));
  }
  if (
    isFulfilled(action) ||
    isRejected(action) ||
    isRejectedWithValue(action)
  ) {
    store.dispatch(setLoading(false));
  }
  return next(action);
};

export const store = configureStore({
  reducer: {
    [QApi.reducerPath]: QApi.reducer,
    [PublicQApi.reducerPath]: PublicQApi.reducer,
    user: userReducer,
    isLoading: loadingReducer,
    isLightTheme: themeReducer,
    role: roleReducer,
    unreadCount: unreadReducer,
    firstLoginModal: firstLoginModalReducer,
    qr: qrReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          'QApi/mutations/downloadQrCodePdf/fulfilled',
          'QApi/mutations/downloadBusinessQrCodePdf/fulfilled',
          'QApi/mutations/generateReport/fulfilled',
          'QApi/mutations/generateInvoice/fulfilled',
          'QApi/mutations/getOrderInvoice/fulfilled',
        ],
        ignoredPaths: [
          'QApi.mutations.downloadQrCodePdf.data',
          'QApi.mutations.downloadBusinessQrCodePdf.data',
          'QApi.mutations.generateReport.data',
          'QApi.mutations.generateInvoice.data',
          'QApi.mutations.getOrderInvoice.data',
        ],
      },
    })
      .concat(QApi.middleware)
      .concat(PublicQApi.middleware)
      .concat(rtkQueryErrorLogger)
      .concat(genericLoaderMiddleware),
});
